import { createI18n } from 'vue-i18n'

import.meta.glob('@/locales/**/*.json')
// Include newly translated languages by default; can be overridden via VITE_SUPPORTED_LANGUAGES
const availableLanguages = (import.meta.env.VITE_SUPPORTED_LANGUAGES || 'en,nl,fa,de,fr,es,it,ar,pt,nb,sv,da').split(',')
let i18n
let loadedLocales = {}
let loadedModules = {}

function deepMerge (target, source) {
  const output = { ...target }

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          output[key] = source[key]
        } else {
          output[key] = deepMerge(target[key], source[key])
        }
      } else {
        output[key] = source[key]
      }
    })
  }

  return output
}

function isObject (item) {
  return (item && typeof item === 'object' && !Array.isArray(item))
}

async function loadLocaleMessages (locale) {
  if (loadedLocales[locale]) {
    return loadedLocales[locale]
  }

  try {
    if (!loadedModules[locale]) {
      loadedModules[locale] = {}
    }

    const modules = import.meta.glob('@/locales/**/*.json')
    let messages = {}

    const localeModules = Object.keys(modules).filter(key => key.startsWith(`/src/locales/${locale}/`))

    if (localeModules.length === 0) {
      loadedLocales[locale] = {}
      return {}
    }

    await Promise.all(localeModules.map(async path => {
      const module = await modules[path]()
      const moduleName = path.split('/').pop().replace('.json', '')
      loadedModules[locale][moduleName] = true
      messages = deepMerge(messages, module.default)
    }))

    loadedLocales[locale] = messages
    return messages
  } catch (error) {
    return {}
  }
}

async function loadLocaleModule (locale, moduleName) {
  try {
    if (!loadedModules[locale]) {
      loadedModules[locale] = {}
    }

    if (loadedModules[locale][moduleName]) {
      return true
    }

    const modules = import.meta.glob('@/locales/**/*.json')
    const modulePath = `/src/locales/${locale}/${moduleName}.json`

    if (!modules[modulePath]) {
      return false
    }

    const module = await modules[modulePath]()

    loadedModules[locale][moduleName] = true

    if (i18n && i18n.global) {
      const currentMessages = i18n.global.getLocaleMessage(locale) || {}
      i18n.global.setLocaleMessage(locale, deepMerge(currentMessages, module.default))
    }

    return true
  } catch (error) {
    return false
  }
}

export async function setupI18n (app, locale = 'en') {
  const messages = await loadLocaleMessages(locale)

  i18n = createI18n({
    legacy: false, locale, fallbackLocale: 'en', messages: {
      [locale]: messages
    }
  })

  await setLanguage(locale)

  app.use(i18n)
  return i18n
}

export function getLocale () {
  return i18n?.global?.locale?.value || 'en'
}

export async function setLanguage (locale) {
  if (!i18n.global.getLocaleMessage(locale) || Object.keys(i18n.global.getLocaleMessage(locale)).length === 0) {
    const messages = await loadLocaleMessages(locale)
    i18n.global.setLocaleMessage(locale, messages)
  }

  i18n.global.locale.value = locale
  document.documentElement.setAttribute('lang', locale)

  if (['ar', 'he', 'fa', 'ur'].includes(locale)) {
    document.documentElement.setAttribute('dir', 'rtl')
  } else {
    document.documentElement.setAttribute('dir', 'ltr')
  }
}

export async function preloadLocaleModules (locale, moduleNames) {
  if (!Array.isArray(moduleNames) || moduleNames.length === 0) {
    return false
  }

  try {
    const results = await Promise.all(moduleNames.map(moduleName => loadLocaleModule(locale, moduleName)))
    const allSuccessful = results.every(result => result === true)
    return allSuccessful
  } catch (error) {
    return false
  }
}

export { availableLanguages, i18n, loadLocaleModule }
